import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {StandaloneComponent} from '../standalone.component';
import {ActivatedRoute, Router} from '@angular/router';
import { AuthenticationInfoService, SessionInfo} from '../authentication-info.service';
import {AppConstants} from '../AppConstants';
import {ParamsService} from '../params.service';
import {Logger} from '../../util/logger';
import {Observable, Subscription} from 'rxjs';
import {Invitation, InvitationService, User} from '../../gen';
import {AbstractControl, FormControl, FormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';

const _ = (s) => s;

interface JoinParams {
  accept?: string;
  decline?: string;
  skip?: string;
  token?: string;
  id?: string;
  email?: string;
}

@Component({
  selector: 'app-join',
  templateUrl: './join.component.html',
  styleUrls: ['./join.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class JoinComponent extends StandaloneComponent implements OnInit, OnDestroy {

  sessionInfo: SessionInfo;
  private destroyed = false;
  private sessionInfoSubscription: Subscription;
  requiredParametersMissing = false;
  parameterMismatch = false;
  /* null, or if mismatched the value of users session email */
  emailMismatch: string = null;
  loadInvitationFailed = false;
  acceptFailed = false;
  declineFailed = false;
  invitation: Invitation;
  private params: JoinParams;
  submitFailed: boolean;
  invitationForm = new FormGroup({
    token: new FormControl('', []),
    email: new FormControl('', [Validators.required, Validators.email]),
  });
  constructor(
    route: ActivatedRoute,
    router: Router,
    hostElement: ElementRef,
    private authnInfoService: AuthenticationInfoService,
    private paramsService: ParamsService,
    private translate: TranslateService,
    private logger: Logger,
    @Inject('InvitationApi') private invitationService: InvitationService,
  ) {
    super(route, router, hostElement);
    this.idOrTokenRequiredValidator = this.idOrTokenRequiredValidator.bind(this);
    this.resolveField('token').setValidators([this.idOrTokenRequiredValidator]);
  }
  idOrTokenRequiredValidator(ctrl: FormControl) {
    let retVal = null;
    if (!this.resolveField('token').value && (!this.params || !this.params.id)) {
      retVal = { 'idOrTokenRequired': {value: ctrl.value} };
    }
    return retVal;
  }
  ngOnInit() {
    super.init();
    this.authnInfoService.startTask('join');
    this.readParams().then((val) => {
      this.params = val;
      if (val) {
        if (val.token) {
          this.resolveField('token').setValue(val.token);
        }
        if (val.email) {
          this.resolveField('email').setValue(val.email);
        }
        if (val.token && val.email) {
          this.loadInvitation();
        } else if (val.id) {
          this.loadInvitation();
        } else {
          this.requiredParametersMissing = true;
        }
        this.resolveField('token').updateValueAndValidity();
        this.invitationForm.updateValueAndValidity();
      }
    });
    this.sessionInfoSubscription = this.authnInfoService.sessionInfo.subscribe(sessnInfo => {
      this.sessionInfo = sessnInfo;
    });
  }
  private loadInvitation(callback?: (inv: Invitation | any) => void) {
    if (this.resolveField('token').value && this.resolveField('email').value) {
      this.invitationService.getInvitationByToken(
        this.resolveField('token').value,
        this.resolveField('email').value,
        'response',
        false
      ).subscribe((inv) => {
        if (callback) {
          callback(inv.body as Invitation);
        } else {
          this.invitationLoaded(inv.body as Invitation);
        }
      }, (err) => {
        if (callback) {
          callback(err);
        } else {
          this.invitationLoadFailed(err);
        }
      });
    } else if (this.params.id) {
      this.invitationService.getInvitationById(this.params.id, 'response', false)
        .subscribe((inv) => {
          if (callback) {
            callback(inv.body as Invitation);
          } else {
            this.invitationLoaded(inv.body as Invitation);
          }
        }, (err) => {
          if (callback) {
            callback(err);
          } else {
            this.invitationLoadFailed(err);
          }
        })
      ;
    } else {
      this.invitationLoadFailed({ error: 'required_parameters_missing' });
    }
  }
  private invitationLoaded(inv: Invitation) {
    if (!this.destroyed) {
      this.logger.debug('invitation loaded: %o', inv);
      this.invitation = inv;
      if (this.params.id && this.params.id !== this.invitation.id) {
        this.parameterMismatch = true;
      }
      if (this.sessionInfo.hasSession) {
        this.authnInfoService.getUser().subscribe((resp) => {
          const user = resp.body as User;
          if (!user.email || user.email !== inv.email) {
            this.emailMismatch = user.email || 'undefined';
            if (user.email) {
              this.emailMismatch = user.email;
            } else {
              this.translate.get('join.undefined-email-label').subscribe((res) => {
                this.emailMismatch = res;
              }, err => {
                this.emailMismatch = 'join.undefined-email-label';
              });
            }
          } else if (this.params.accept && this.params.accept === this.params.token && this.params.email) {
            this.accept();
          }
        });
      }
    }
  }
  private invitationLoadFailed(err) {
    this.logger.debug('loading invitation failed %o', err);
    this.loadInvitationFailed = true;
  }
  ngOnDestroy() {
    this.destroyed = true;
    this.sessionInfoSubscription.unsubscribe();
    if (this.authnInfoService.sessionInfo.value.tasksInProgress.indexOf('join') >= 0) {
      this.authnInfoService.cancelTask('join');
    }
  }
  private resolveField(s: string): AbstractControl {
    return this.invitationForm.get(s);
  }
  private resolvePathForSession(): string {
    let retVal = null;
    if (this.invitation && this.invitation.memberStatus !== 'user') {
      retVal = AppConstants.PATH_REGISTER;
    } else {
      retVal = AppConstants.PATH_LOGIN;
    }
    return retVal;
  }
  signOut() {
    this.authnInfoService.cancelTask('join');
    this.router.navigate([AppConstants.PATH_LOGOUT], {
      relativeTo: this.route,
    });
  }
  close() {
    this.authnInfoService.completeTask('join');
    if (!this.sessionInfo.fullyAuthenticated) {
      const qp = {
        [AppConstants.QP_INVITATION_TOKEN]: null,
        [AppConstants.QP_INVITATION_ID]: null,
        [AppConstants.QP_EMAIL]: this.resolveField('email').value,
      };
      this.router.navigate([this.resolvePathForSession()], {
        relativeTo: this.route,
        queryParamsHandling: 'merge',
        queryParams: qp,
      });
    } else if (this.requiredParametersMissing || this.loadInvitationFailed || this.emailMismatch) {
      // App.component triggers a redirect to next before this is hit, when applicable.
      // Hopefully always, but at least in all the test cases. This redirect should probably be conditional,
      // but we don't have proper data here at the moment.
      const qp = {};
      this.router.navigate([AppConstants.PATH_PROFILE], {
        relativeTo: this.route,
        queryParams: qp,
      });
    }
  }
  accept() {
    this.logger.debug('accept');
    if (this.params.id) {
      this.invitationService.acceptInvitationById(this.params.id, 'response', false)
        .subscribe(() => {
          this.authnInfoService.completeTask('join');
          this.authnInfoService.fetchAuthenticationInfo();
        }, (err) => {
          this.acceptFailed = true;
        })
      ;
    } else if (this.sessionInfo.hasSession) {
      this.invitationService.acceptInvitation(
        this.resolveField('token').value,
        this.resolveField('email').value,
        'response',
        false
      ).subscribe(() => {
        this.authnInfoService.completeTask('join');
        this.authnInfoService.fetchAuthenticationInfo();
      }, (err) => {
        this.acceptFailed = true;
      });
    } else {
      this.invitationService.getInvitationByToken(
        this.resolveField('token').value,
        this.resolveField('email').value,
        'response',
        false
      ).subscribe((inv) => {
        this.params.accept = null;
        this.invitationLoaded(inv.body as Invitation);
        const qp = {
          [AppConstants.QP_REQUIRE_CHALLENGE]: AppConstants.AC_AM_INVITATION_ACCEPTED,
          [AppConstants.QP_INVITATION_TOKEN]: this.resolveField('token').value,
          [AppConstants.QP_EMAIL]: this.resolveField('email').value,
        };
        this.authnInfoService.completeTask('join');
        this.router.navigate([this.resolvePathForSession()], {
          relativeTo: this.route,
          queryParamsHandling: 'merge',
          queryParams: qp,
        });
      }, (err) => {
        this.acceptFailed = true;
      });
    }
  }
  decline() {
    this.logger.debug('decline');
    const declinedHandler = () => {
      this.authnInfoService.completeTask('join');
      if (!this.sessionInfo.hasSession) {
        const qp = {
          [AppConstants.QP_INVITATION_ID]: null,
          [AppConstants.QP_INVITATION_TOKEN]: null,
          [AppConstants.QP_EMAIL]: this.resolveField('email').value,
        };
        this.router.navigate([this.resolvePathForSession()], {
          relativeTo: this.route,
          queryParamsHandling: 'merge',
          queryParams: qp,
        });
      } else {
        this.authnInfoService.fetchAuthenticationInfo();
      }
    };
    if (this.params.id) {
      this.invitationService.declineInvitationById(this.params.id, 'response', false)
        .subscribe(declinedHandler, (err) => { this.declineFailed = true; })
      ;
    } else {
      this.invitationService.declineInvitation(
        this.resolveField('token').value,
        this.resolveField('email').value,
        'response',
        false
      ).subscribe(declinedHandler, (err) => { this.declineFailed = true; });
      this.router.navigate([AppConstants.PATH_HOME], {
      });
    }
  }
  skip() {
    this.logger.debug('skip');
    if (this.invitation) {
      this.authnInfoService.skipInvitation(this.invitation.id);
      this.authnInfoService.completeTask('join');
      if (!this.sessionInfo.hasSession) {
        const qp = {
          [AppConstants.QP_INVITATION_TOKEN]: null,
          [AppConstants.QP_INVITATION_ID]: null,
          [AppConstants.QP_EMAIL]: this.resolveField('email').value,
        };
        this.router.navigate([this.resolvePathForSession()], {
          relativeTo: this.route,
          queryParamsHandling: 'merge',
          queryParams: qp,
        });
      }
    } else {
      this.loadInvitation((inv: Invitation | any) => {
        if (inv && inv.id) {
          this.invitationLoaded(inv);
          this.skip();
        } else {
          this.invitationLoadFailed(inv);
        }
      });
    }
  }
  allowSkip(): boolean {
    // Currently always skippable
    return true;
  }
  invitationCopy(): string {
    return !this.requiredParametersMissing && this.invitation ? this.invitation.invitationScopeInformation : null;
  }
  private readParams(): Promise<JoinParams> {
    return new Promise<JoinParams>((resolve) => {
      const acceptPromise = this.paramsService.getParam(AppConstants.QP_HANDLE_CHALLENGE);
      const tokenPromise = this.paramsService.getParam(AppConstants.QP_INVITATION_TOKEN);
      const emailPromise = this.paramsService.getParam(AppConstants.QP_EMAIL);
      const idPromise = this.paramsService.getParam(AppConstants.QP_INVITATION_ID);
      Promise.all([acceptPromise, tokenPromise, emailPromise, idPromise]).then((values) => {

        const result: JoinParams = {
          accept: values[0] === AppConstants.AC_AM_INVITATION_ACCEPTED ? values[1] : null,
          token: values[1],
          email: values[2],
          id: values[3],
        };

        resolve(result);
      });
    });
  }
  public resolveWindowTitlePart(): Observable<string|undefined> {
    return this.translate.get(_('join.window-title'));
  }
}
